import _ from 'lodash-es';
import Action from '~/scripts/services/model/action';
import Droit from '~/scripts/services/model/droit';
import LinkStyle from '~/scripts/services/model/link-style';
import Node from '~/scripts/services/model/node';
import Repre from '~/scripts/services/model/repre';
import State from '~/scripts/services/model/state';
import Utils from '~/scripts/services/utils';
import Defaults from '~/scripts/constants/defaults';
import Card from '~/scripts/services/model/card';

export default class Workflow extends Node {
	#$injector;

	#$translate;

	#User;

	#loading;

	/** @type {any} */
	#replaceKids;

	/* @ngInject */
	/**
	 * Instantiates an Workflow
	 *
	 * @param {import('angular').auto.IInjectorService} $injector Injector dependency
	 * @param {import('@knitiv/api-client-javascript').KNode | undefined} kid Optional KID of the workflow
	 */
	constructor($injector, kid = undefined) {
		super($injector, kid, 'K_NODE;WF_DESIGNER_SCHEMA');
		this.#$injector = $injector;
		this.#$translate = $injector.get('$translate');
		this.#User = $injector.get('User');

		/** @type {{width: number | undefined, height: number | undefined}} */
		this.area = {
			width:  undefined,
			height: undefined,
		};

		/** @type {Action[]} */
		this.actions = [];

		/** @type {boolean} */
		this.create = !kid;

		/**
		 * @type {{kid: import('@knitiv/api-client-javascript').KNode,
		 * name: string,
		 * isa: import('@knitiv/api-client-javascript').KNode}[]}
		 */
		this.kidlist = [];

		/** @type {boolean} */
		this.#loading = false;

		/** @type {any[]} */
		this.roles = [];

		/** @type {string | undefined} */
		this.state = undefined;

		/** @type {boolean} */
		this.stateAffectAuto = false;

		/** @type {State[]} */
		this.states = [];

		if (this.create) {
			this.addRepre(new Repre({
				value: this.#$translate.instant('workflow.new-wf'),
			}));
		}

		this.name = this.getName();

		this.#replaceKids = {};
	}

	/**
	 * @param {any} actionlist
	 */
	set actionlist(actionlist) {
		for (const action of actionlist) {
			this.addAction(action);
		}
	}

	/**
	 * @param {any} droit
	 */
	set droitlist(droit) {
		for (const d of droit) {
			(async () => this.addDroit(d))();
		}
	}

	/**
	 * @param {any} statelist
	 */
	set statelist(statelist) {
		for (const state of statelist) {
			this.addState(state);
		}
	}

	/**
	 * Adds an Action
	 *
	 * @param {any} action Action details
	 * @param {boolean} duplicate should duplicate for renew KID ?
	 * @returns {Action} new Action instance
	 */
	addAction(action, duplicate = false) {
		/** @type {Action} */
		const newAction = new Action(this.#$injector);
		newAction.loading = this.#loading;

		newAction.affectAuto = action.affectAuto;
		if (action.kid && duplicate) {
			const oldActionKid = action.kid;
			action.kid = `K_NODE;${this.#User.getRefNr()}`;
			this.#replaceKids[oldActionKid] = action.kid;
		}

		newAction.id = action.id || (action.kid && action.kid.split(';')[1]);
		newAction.kid = action.kid;

		try {
			newAction.kjson = typeof action.kjson === 'string' ? JSON.parse(action.kjson) : action.kjson;
		}
		catch {
			newAction.kjson = null;
		}

		if ('name' in action && action.name) {
			newAction.repre.push(new Repre({
				kid:   `K_NODE;${this.#User.getRefNr()}`,
				value: action.name,
			}));
		}
		else if ('repre' in action) {
			for (const actionRepre of action.repre) {
				newAction.addRepre(new Repre(actionRepre));
			}
		}

		if ('droit' in action) {
			for (const droit of action.droit) {
				// Retrieve isa/name from kidlist
				const kdroit = this.kidlist.find(index => index.kid === (droit.role?.kid || droit.ref_role));
				if (kdroit) {
					droit.role_isa = kdroit.isa;
					droit.role_name = kdroit.name;
				}

				// Retrieve org isa/name from kidlist
				if (droit.org_kid) {
					const kdroitorg = this.kidlist.find(index => index.kid === droit.org_kid);
					if (kdroitorg) {
						droit.org_isa = kdroitorg.isa;
						droit.org_name = kdroitorg.name;
					}
				}

				newAction.droit.push(new Droit(droit));
			}
		}

		if ('droitcust' in action) {
			for (const droitcust of action.droitcust) {
				newAction.addDroitCust({
					index:    droitcust.index,
					kright:   droitcust.kright,
					order:    droitcust.order,
					org:      droitcust.org || { kid: droitcust.org_kid },
					role:     droitcust.role || { kid: droitcust.role_kid },
					suborder: droitcust.suborder,
				});
			}
		}

		if ('droitdef' in action) {
			for (const droitdef of action.droitdef) {
				newAction.addDroitDef({
					index:    droitdef.index,
					kright:   droitdef.kright,
					order:    droitdef.order,
					org:      droitdef.org || { kid: droitdef.org_kid },
					role:     droitdef.role || { kid: droitdef.role_kid },
					suborder: droitdef.suborder,
				});
			}
		}

		for (const state of action.statefromlist) {
			if (duplicate && state in this.#replaceKids) {
				newAction.statefrom[this.#replaceKids[state]] = undefined;
			}
			else {
				newAction.statefrom[state] = undefined;
			}
		}
		for (const state of action.statetolist) {
			if (duplicate && state in this.#replaceKids) {
				newAction.stateto[this.#replaceKids[state]] = undefined;
			}
			else {
				newAction.stateto[state] = undefined;
			}
		}
		newAction.style = Object.assign(new LinkStyle(), action.style);
		newAction.x = Number.isNaN(Number.parseInt(action.x, 10)) ? 0 : Number.parseInt(action.x, 10);
		newAction.y = Number.isNaN(Number.parseInt(action.y, 10)) ? 0 : Number.parseInt(action.y, 10);

		newAction.allocators = action.allocatorlist || [];
		if ('rolelist' in action) {
			for (const role of action.rolelist) {
				newAction.allocators.push(
					role && typeof role === 'object' ? role.kid : role,
				);
			}
		}

		if ('profillist' in action) {
			for (const profil of action.profillist) {
				newAction.allocators.push(
					profil && typeof profil === 'object' ? profil.kid : profil,
				);
			}
		}
		
		this.actions.push(newAction);
		newAction.loading = false;

		return newAction;
	}

	/**
	 * Adds a delegation profile in schema
	 *
	 * @param {any} delegationGranted Delegation granted profile
	 */
	addDelegationGranted(delegationGranted) {
		this.kidlist.push(delegationGranted);
	}

	/**
	 * @param {any} droit
	 * @param {?import('@knitiv/api-client-javascript').KNode} [kid]
	 * @returns {Node} The current node
	 */
	// eslint-disable-next-line no-unused-vars
	addDroit(droit, kid) {
		// Retrieve isa/name from kidlist
		const kdroit = this.kidlist.find(index => index.kid === (droit.role?.kid || droit.ref_role));
		if (kdroit) {
			droit.role_isa = kdroit.isa;
			droit.role_name = kdroit.name;
		}
		droit.kid = kid;

		return super.addDroit(droit);
	}

	/**
	 * Adds a role in schema
	 *
	 * @param {any} role Role details
	 */
	addRole(role) {
		this.kidlist.push(role);
	}

	/**
	 * Adds a State
	 *
	 * @param {any} state State details
	 * @param {boolean} duplicate should duplicate for renew KID ?
	 * @returns {State} new State instance
	 */
	addState(state, duplicate = false) {
		const newState = new State(this.#$injector);
		newState.loading = this.#loading;

		if (state.kid && duplicate) {
			const oldStateKid = state.kid;
			state.kid = `K_NODE;${this.#User.getRefNr()}`;
			this.#replaceKids[oldStateKid] = state.kid;
		}

		newState.setKid(state.kid);
		newState.affectAuto = state.affectAuto;
		newState.color = state.color;
		newState.delegationGranted = state.delegationGranted;
		newState.initial = Number.isNaN(Number.parseInt(state.initial, 10)) ? 0 : Number.parseInt(state.initial, 10);
		newState.id = state.id || (state.kid && state.kid.split(';')[1]);
		newState.kid = state.kid;

		try {
			newState.kjson = typeof state.kjson === 'string' ? JSON.parse(state.kjson) : state.kjson;
		}
		catch {
			newState.kjson = null;
		}

		newState.notify = !!state.notify;
		newState.notifyInitiator = !!state.notifyInitiator;

		if ('kndef' in state) {
			if (!('KWF' in state.kndef)) {
				state.kndef = {
					KWF: state.kndef,
				};
			}
			_.merge(newState.kndef, state.kndef);
		}

		if ('name' in state && state.name) {
			newState.repre.push(new Repre({
				kid:   `K_NODE;${this.#User.getRefNr()}`,
				value: state.name,
			}));
		}
		else if ('repre' in state) {
			for (const repre of state.repre) {
				newState.loading = true;
				newState.addRepre(new Repre(repre));
				newState.loading = false;
			}
		}

		if ('droit' in state) {
			for (const droit of state.droit) {
				// Compability droit.role_kid / droit.ref_role
				if ('role_kid' in droit) {
					droit.ref_role = droit.role_kid;
					delete droit.role_kid;
				}

				// Retrieve isa/name from kidlist
				const kdroit = this.kidlist.find(index => index.kid === droit.ref_role);
				if (kdroit) {
					droit.role_isa = kdroit.isa;
					droit.role_name = kdroit.name;
				}

				// Retrieve org isa/name from kidlist
				if (droit.org_kid) {
					const kdroitorg = this.kidlist.find(index => index.kid === droit.org_kid);
					if (kdroitorg) {
						droit.org_isa = kdroitorg.isa;
						droit.org_name = kdroitorg.name;
					}
				}

				newState.droit.push(new Droit(droit));
			}
		}

		if ('droitcust' in state) {
			for (const droitcust of state.droitcust) {
				newState.addDroitCust({
					index:    droitcust.index,
					kright:   droitcust.kright,
					order:    droitcust.order,
					org:      droitcust.org || { kid: droitcust.org_kid },
					role:     droitcust.role || { kid: droitcust.role_kid },
					suborder: droitcust.suborder,
				});
			}
		}

		if ('droitdef' in state) {
			for (const droitdef of state.droitdef) {
				newState.addDroitDef({
					index:    droitdef.index,
					kright:   droitdef.kright,
					order:    droitdef.order,
					org:      droitdef.org || { kid: droitdef.org_kid },
					role:     droitdef.role || { kid: droitdef.role_kid },
					suborder: droitdef.suborder,
				});
			}
		}

		newState.observCard = new Card(state.observ_card ?? state.observCard ?? '0-0');
		newState.shape = state.shape || 'RECTANGLE';
		newState.x = Number.isNaN(Number.parseInt(state.x, 10)) ? 0 : Number.parseInt(state.x, 10);
		newState.y = Number.isNaN(Number.parseInt(state.y, 10)) ? 0 : Number.parseInt(state.y, 10);

		newState.allocators = state.allocatorlist || [];
		if ('rolelist' in state) {
			for (const role of state.rolelist) {
				newState.allocators.push(
					role && typeof role === 'object' ? role.kid : role,
				);
			}
		}

		if ('profillist' in state) {
			for (const profil of state.profillist) {
				newState.allocators.push(
					profil && typeof profil === 'object' ? profil.kid : profil,
				);
			}
		}

		this.states.push(newState);
		newState.loading = false;

		return newState;
	}

	/**
	 * Automatically places the node on schema
	 */
	autoplaceNodes() {
		if (this.area.width && this.area.height) {
			let gx = 50;
			let
				gy = 25;
			const nodes = Utils.shuffle([
				...this.states,
				...this.actions,
			]);
			for (const node of nodes) {
				if (node.x === 0 && node.y === 0) {
					node.x = gx;
					node.y = gy;

					if (gx > this.area.width - 150) {
						gx = 25;
						gy += 100;
					}
					else {
						gx += 150;
					}
				}
			}
		}
	}

	/**
	 * Export the workflow
	 *
	 * @returns {Object} Workflow
	 */
	export() {
		const states = [];
		const actions = [];
		const kidlist = [];

		for (const state of this.states) {
			if (state.visible) {
				states.push({
					affectAuto:        state.affectAuto,
					allocatorlist:     state.allocators,
					color:             state.color,
					delegationGranted: state.delegationGranted,
					droit:             state.droit.map(droit => droit.export()),
					droitcust:         state.droitcust.map(droitcust => droitcust.export()),
					droitdef:          state.droitdef.map(droitdef => droitdef.export()),
					kid:               state.kid,
					kjson:             state.kjson,
					kndef:             state.kndef,
					id:                state.id,
					initial:           state.initial,
					notify:            !!state.notify,
					notifyInitiator:   !!state.notifyInitiator,
					observCard:        `${state.observCard.from}-${state.observCard.to === Defaults.KCARDN
						? 'N' : state.observCard.to}`,
					repre:             state.repre,
					shape:             state.shape,
					x:                 state.x,
					y:                 state.y,
				});
			}
		}

		for (const action of this.actions) {
			if (action.visible) {
				actions.push({
					affectAuto:    action.affectAuto,
					allocatorlist: action.allocators,
					droit:         action.droit.map(droit => droit.export()),
					droitcust:     action.droitcust.map(droitcust => droitcust.export()),
					droitdef:      action.droitdef.map(droitdef => droitdef.export()),
					kid:           action.kid,
					kjson:         action.kjson,
					id:            action.id,
					repre:         action.repre,
					shape:         action.shape,
					statefromlist: Object.keys(action.statefrom),
					statetolist:   Object.keys(action.stateto),
					style:         action.style,
					x:             action.x,
					y:             action.y,
				});
			}
		}

		for (const item of this.kidlist) {
			kidlist.push({
				isa:  item.isa,
				kid:  item.kid,
				name: item.name,
			});
		}

		return {
			actionlist: actions,
			droit:      this.droit.map(droit => droit.export()),
			kid:        this.kid,
			repre:      this.repre,
			kidlist,
			statelist:  states,
		};
	}

	/**
	 * Get the index from the actions array
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} kid Identifier of the node
	 * @returns {number} Index
	 */
	getKeyActionFromKid(kid) {
		let key = -1;
		for (const [k, action] of this.actions.entries()) {
			if (action.kid === kid && action.visible) {
				key = k;
			}
		}
		return key;
	}

	/**
	 * Get the key from the states array
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} kid Identifier of the node
	 * @returns {number} Index of the state
	 */
	getKeyStateFromKid(kid) {
		return this.states.findIndex(state => state.kid === kid && state.visible);
	}

	/**
	 * Get the state from the states array
	 *
	 * @param {string} id Internal identifier of the node
	 * @returns {State | undefined} State
	 */
	getState(id) {
		return this.states.find(state => state.id === id && state.visible);
	}

	/**
	 * @param {any} data
	 */
	load(data) {
		this.#loading = true;

		for (const [klink, action] of Object.entries(data.actionlist || {})) {
			if (klink.startsWith('KLINK;')) {
				action.klink = klink;
			}
			this.addAction(action);
		}

		for (const state of Object.values(data.statelist || {})) {
			this.addState(state);
		}

		if ('droit' in data) {
			for (const droit of data.droit) {
				this.droit.push(new Droit(droit));
			}
		}

		_.merge(this.repre, data.repre);
		this.#loading = false;
	}

	/**
	 * Set the representations of the schema from the given array
	 *
	 * @param {Repre[] | any[]} repres Array of Repre instances
	 * @param {boolean} duplicate should duplicate for renew KID ?
	 */
	setRepre(repres, duplicate = false) {
		if (duplicate) {
			for (const repre of repres) {
				if (repre.kid) {
					const oldRepreKid = repre.kid;
					repre.kid = `K_NODE;${this.#User.getRefNr()}`;
					this.#replaceKids[oldRepreKid] = repre.kid;
				}
			}
		}
		else {
			this.repre = repres;
		}
	}
}
